import { Truck, Check, Package, MapPin, ShieldCheck, PenLine, Clock } from "lucide-react";
import { AVAILABLE_SHIPPING_OPTIONS, type ShippingOption } from "@/lib/shipping";
import { formatGBP } from "@/lib/format";

/**
 * Buyer-facing shipping summary shown on auction detail pages.
 * Presentation-only: reads existing SHIPPING_OPTIONS metadata and shows
 * feature ticks that are actually supported by each service. No pricing,
 * tracking or compensation claims are invented — everything comes from
 * the existing shipping catalogue.
 */

type DispatchDays = 0 | 1 | 2 | 5;

export function formatDispatch(days: number | null | undefined): string | null {
  if (days == null || Number.isNaN(days)) return null;
  if (days <= 0) return "Same business day";
  if (days === 1) return "Next business day";
  if (days === 2) return "Within 2 business days";
  if (days <= 5) return "Within 3–5 business days";
  return null;
}

function iconFor(option: ShippingOption) {
  if (option.provider === "collection") return MapPin;
  if (option.signature) return ShieldCheck;
  if (option.id === "royal_mail_tracked_24") return Clock;
  return Package;
}

function featureList(option: ShippingOption): { label: string; key: string }[] {
  const features: { label: string; key: string }[] = [];
  if (option.tracked) features.push({ key: "tracking", label: "Tracking included" });
  if (option.signature) features.push({ key: "signature", label: "Signature required" });
  if (option.compensationPence && option.compensationPence > 0) {
    features.push({
      key: "compensation",
      label: `Compensation available up to ${formatGBP(option.compensationPence)}`,
    });
  }
  if (!option.tracked && option.provider === "collection") {
    features.push({ key: "in-person", label: "Handover in person" });
  }
  return features;
}

export function ShippingSummary({ dispatchDays }: { dispatchDays?: number | null } = {}) {
  const services = AVAILABLE_SHIPPING_OPTIONS.filter((o) => o.id !== "international_tracked");
  const dispatch = formatDispatch(dispatchDays);

  return (
    <section
      aria-labelledby="shipping-summary-heading"
      className="mt-6 rounded-xl border border-border/60 bg-surface-2/40 p-4 sm:p-5"
    >
      <header className="flex items-center gap-2">
        <Truck className="h-4 w-4 text-primary" aria-hidden="true" />
        <h2
          id="shipping-summary-heading"
          className="font-display text-lg text-foreground"
        >
          Shipping
        </h2>
      </header>

      <p className="mt-1 text-xs text-muted-foreground">
        Choose your preferred service at checkout. Only supported features are shown for each option.
      </p>

      <ul className="mt-4 grid gap-3 sm:grid-cols-2" role="list">
        {services.map((option) => {
          const Icon = iconFor(option);
          const features = featureList(option);
          return (
            <li
              key={option.id}
              className="flex h-full flex-col gap-2 rounded-lg border border-border/60 bg-background/40 p-3"
              aria-label={`${option.label}. ${features.map((f) => f.label).join(". ")}`}
            >
              <div className="flex items-start gap-2">
                <Icon
                  className="mt-0.5 h-4 w-4 shrink-0 text-primary/80"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug text-foreground">
                    {option.label}
                  </p>
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    {option.providerLabel}
                  </p>
                </div>
              </div>
              {features.length > 0 ? (
                <ul className="mt-auto space-y-1 text-xs" role="list">
                  {features.map((f) => (
                    <li key={f.key} className="flex items-start gap-1.5 text-muted-foreground">
                      <Check
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400"
                        aria-hidden="true"
                      />
                      <span className="text-foreground/90">{f.label}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>

      {dispatch ? (
        <div
          className="mt-4 flex items-center gap-2 rounded-md border border-border/60 bg-background/40 px-3 py-2 text-xs"
          aria-live="polite"
        >
          <PenLine className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-muted-foreground">Dispatch:</span>
          <span className="font-medium text-foreground">{dispatch}</span>
        </div>
      ) : null}
    </section>
  );
}
